import {getConfig} from './config'
import {fetchAuthSession} from 'aws-amplify/auth'

type LogFn = (message: string, props?: Record<string, any>) => void

interface Logger {
  logDebug: LogFn
  logError: LogFn
  logInfo: LogFn
  logWarn: LogFn
}

export async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession()
  return session.tokens.idToken.toString()
}

const {loggingEnabled, loggingLevel, loggingApiUrl} = getConfig()

/**
 * @class DefaultLogger
 * @classdesc Logger implementation interfaces with an API backend for server-persisted UI logging
 */
class DefaultLogger implements Logger {
  private readonly module: string = ''

  public constructor(module: string) {
    this.module = module
  }

  public static isLoggableLevel(logLevel: string): boolean {
    const loggingLevelValue = {
      'debug': 3,
      'info': 2,
      'warn': 1,
      'error': 0
    }
    const logValue =
      loggingLevelValue[logLevel as keyof typeof loggingLevelValue] || 0
    const logValueThreshold =
      loggingLevelValue[loggingLevel as keyof typeof loggingLevelValue]
    return logValue <= logValueThreshold
  }

  public logDebug(message: string, props: Record<string, any> = {}): void {
    console.debug(message, props)
    this.log('debug', message, props)
  }

  public logError(message: string, props: Record<string, any> = {}): void {
    console.error(message, props)
    this.log('error', message, props)
  }

  public logInfo(message: string, props: Record<string, any> = {}): void {
    console.info(message, props)
    this.log('info', message, props)
  }

  public logWarn(message: string, props: Record<string, any> = {}): void {
    console.warn(message, props)
    this.log('warn', message, props)
  }

  private async log(
    logLevel: string,
    message: string,
    props: Record<string, any>
  ): Promise<void> {
    try {
      if (loggingEnabled && DefaultLogger.isLoggableLevel(logLevel)) {
        const token = await getAuthToken()
        await fetch(loggingApiUrl, {
          method: 'post',
          headers: new Headers({
            'Authorization': token,
            'Content-Type': 'application/json',
            'Accept': '*/*'
          }),
          body: JSON.stringify({
            module: this.module,
            logLevel,
            message,
            props
          })
        })
      }
    } catch (e) {
      console.error('Logging error', e)
    }
  }
}

export function getLogger(module = 'global'): Logger {
  return new DefaultLogger(module)
}
